/**
 * PartyBillReturnsScreen — Party Bill Returns (4.21.3)
 * Goods returned to the supplier against a previously entered Party Bill.
 * Selecting a Party Bill (via ERPLookupModal) hydrates the header + Item Info grid;
 * only the "Qty Return" column is editable (capped conceptually by the ordered qty).
 */
import React, { useState, useMemo } from 'react';
import { Save, Grid3X3, XCircle, Search } from 'lucide-react';
import ERPAdvancedGrid from '../../../components/erp/ERPAdvancedGrid';
import ERPEditableGrid from '../../../components/erp/ERPEditableGrid';
import ERPLookupModal from '../../../components/erp/ERPLookupModal';
import DynamicFieldRenderer from '../../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../../components/erp/ERPToast';
import { FormHelp } from '../../../components/help-engine/FormHelp';
import { HelpInfo } from '../../../components/help-engine/HelpInfo';
import '../../../styles/erp-theme.css';
import { tkn, fmtN, today, PARTY_BILL_COLUMNS, fetchPartyBills } from './usePartyBillMasters';

const computeRowValue = (row) => {
  const qty = parseFloat(row.qtyReturn) || 0, rate = parseFloat(row.rate) || 0;
  const taxable = qty * rate;
  const gstAmt = taxable * (parseFloat(row.gstPer) || 0) / 100;
  return { ...row, value: (taxable + gstAmt).toFixed(2) };
};

const itemColumns = () => [
  { field: 'itemCode', header: 'Item Code', width: 90, readOnly: true },
  { field: 'itemName', header: 'Item Name', width: 150, readOnly: true },
  { field: 'hsnCode', header: 'HSN Code', width: 80, readOnly: true },
  { field: 'qtyOrdered', header: 'Qty (PB)', width: 75, readOnly: true },
  { field: 'qtyReturn', header: 'Qty Return', width: 85, type: 'number' },
  { field: 'rate', header: 'Rate', width: 80, readOnly: true },
  { field: 'gstPer', header: 'GST %', width: 60, readOnly: true },
  { field: 'value', header: 'Return Value', width: 100, readOnly: true },
];

const REASON_OPTIONS = ['Damaged Goods', 'Wrong Item Supplied', 'Quality Issue', 'Excess Quantity', 'Other'];

// ═══════════════════════════════════════════════════════
const PBReturnsGrid = ({ rows, onAddNew, onView }) => {
  const columns = [
    { field: 'pbrNo', header: 'PBR No' }, { field: 'pbrDate', header: 'PBR Date' }, { field: 'pbNo', header: 'Against PB' },
    { field: 'supplierName', header: 'Supplier' }, { field: 'returnAmount', header: 'Return Amt' }, { field: 'status', header: 'Status' },
  ];
  return <ERPAdvancedGrid title="Party Bill Returns" columns={columns} data={rows} total={rows.length} idField="pbrNo" nameField="supplierName" page={1} pageSize={20} onPageChange={() => {}} onPageSizeChange={() => {}} loading={false} onAddNew={onAddNew} onEdit={(row) => onView(row)} testId="pb-returns-grid" showFilters={false} cellRenderer={(field, value) => (field === 'returnAmount' ? <span style={{ fontFamily: 'var(--erp-font-mono)' }}>{fmtN(value)}</span> : null)} />;
};

// ═══════════════════════════════════════════════════════
const PBReturnsForm = ({ editData, mode, onGridView, onSaved }) => {
  const isView = mode === 'view';
  const token = tkn();
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();

  const [pbrNo, setPbrNo] = useState(editData?.pbrNo || '');
  const [pbrDate, setPbrDate] = useState(editData?.pbrDate || today());
  const [reason, setReason] = useState('Damaged Goods');
  const [remarks, setRemarks] = useState('');
  const [selectedPB, setSelectedPB] = useState(null);
  const [itemRows, setItemRows] = useState([]);
  const [dynValues, setDynValues] = useState({});
  const [showPBLookup, setShowPBLookup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleRowsChange = (newRows) => setItemRows(newRows.map(computeRowValue));

  const handlePBSelect = (pb) => {
    setSelectedPB(pb);
    setItemRows((pb.items || []).map(it => computeRowValue({ itemCode: it.itemCode, itemName: it.itemName, hsnCode: it.hsnCode, qtyOrdered: it.qty, qtyReturn: '0', rate: it.rate, gstPer: it.gstPer })));
    setShowPBLookup(false);
  };

  const returnAmount = useMemo(() => itemRows.reduce((s, r) => s + parseFloat(r.value || 0), 0), [itemRows]);

  const handleClear = () => { setReason('Damaged Goods'); setRemarks(''); setSelectedPB(null); setItemRows([]); setError(''); };

  const handleSave = () => {
    if (!selectedPB) { setError('Select a Party Bill to return against'); erpToast.warning('Party Bill reference is required'); return; }
    if (!itemRows.some(r => parseFloat(r.qtyReturn) > 0)) { setError('Enter a return quantity for at least one item'); return; }
    setSaving(true); setError('');
    const newPbrNo = pbrNo || `PBR${Date.now().toString().slice(-8)}`;
    setPbrNo(newPbrNo);
    onSaved({ pbrNo: newPbrNo, pbrDate, pbNo: selectedPB.pbNo, supplierName: selectedPB.supplierName, returnAmount: returnAmount.toFixed(2), status: 'Posted', items: itemRows });
    erpToast.success(`Party Bill Return ${newPbrNo} saved against ${selectedPB.pbNo} (backend persistence pending next phase)`);
    setSaving(false);
  };

  return (
    <FormHelp formName="PartyBillReturnsScreen" userRole={userRole}>
    <div className="erp-module" data-testid="pb-returns-form" data-dev-component="PartyBillReturnsScreen" data-dev-file="screens/procurement/party-bill/PartyBillReturnsScreen.js">
      <div className="erp-screen-header">
        {onBack ? (<button onClick={onBack} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Party Bill Returns</span></button>) : (<span className="erp-screen-title">Party Bill Returns</span>)}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)', background: 'var(--erp-hover)', padding: '2px 12px', borderRadius: 4 }} data-testid="pbr-no-display">{pbrNo || 'Auto-Generated'}</div>
          <input type="date" value={pbrDate} onChange={e => setPbrDate(e.target.value)} disabled={isView} className="erp-input" style={{ width: 140, height: 26 }} data-testid="pbr-date-header" />
        </div>
        <div className="erp-screen-actions">
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving} data-testid="pbr-save-btn"><Save size={13} /> {saving ? 'Saving...' : 'Save'}</button>}
          <button className="erp-btn erp-btn-ghost" onClick={onGridView} data-testid="pbr-gridview-btn"><Grid3X3 size={13} /> Gridview</button>
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleClear} data-testid="pbr-clear-btn"><XCircle size={13} /> Clear</button>}
        </div>
      </div>

      {error && <div className="erp-alert-error" data-testid="pbr-error">{error}</div>}

      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }}>
        <div className="erp-form-card" style={{ marginBottom: 8 }}>
          <div className="erp-form-section">
            <div className="erp-form-3col">
              <div>
                <div className="erp-form-row"><label className="erp-form-label required">Party Bill No<HelpInfo controlId="pbNo" controlName="Party Bill No" /></label><div className="erp-form-control" style={{ gap: 4 }}><input value={selectedPB?.pbNo || ''} readOnly placeholder="Search Party Bill..." className="erp-input" style={{ flex: 1 }} onClick={() => !isView && setShowPBLookup(true)} data-testid="pbr-pbno-input" /><button className="erp-btn-icon" onClick={() => !isView && setShowPBLookup(true)} data-testid="pbr-pbno-search-btn"><Search size={13} /></button></div></div>
                <div className="erp-form-row"><label className="erp-form-label">Return Reason<HelpInfo controlId="reason" controlName="Return Reason" /></label><div className="erp-form-control"><select value={reason} onChange={e => setReason(e.target.value)} disabled={isView} className="erp-input erp-select" data-testid="pbr-reason-select">{REASON_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}</select></div></div>
              </div>
              <div>
                <div className="erp-form-row"><label className="erp-form-label">Remarks<HelpInfo controlId="remarks" controlName="Remarks" /></label><div className="erp-form-control"><input value={remarks} onChange={e => setRemarks(e.target.value)} disabled={isView} className="erp-input" data-testid="pbr-remarks-input" /></div></div>
              </div>
              <div>
                <div style={{ border: '1px solid var(--erp-border)', borderRadius: 'var(--erp-radius)', padding: 'var(--erp-sp-2)', fontSize: 11 }} data-testid="pbr-pb-info-panel">
                  <div style={{ fontWeight: 600, marginBottom: 4, textDecoration: 'underline' }}>Party Bill Info</div>
                  <div>Supplier: <strong>{selectedPB?.supplierName || '—'}</strong></div>
                  <div>PB Date: <strong>{selectedPB?.pbDate || '—'}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DynamicFieldRenderer screenName="PartyBillReturnsScreen" recordId={pbrNo} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />

        <div className="erp-form-card" style={{ marginBottom: 8, marginTop: 8 }}>
          <div style={{ padding: 'var(--erp-sp-2)', fontWeight: 600, fontSize: 12, color: 'var(--erp-primary)' }}>Item Info {!selectedPB && <span style={{ fontWeight: 400, color: 'var(--erp-text-muted)' }}>— select a Party Bill to load items</span>}</div>
          <ERPEditableGrid columns={itemColumns()} rows={itemRows} onChange={handleRowsChange} idField="itemCode" testId="pbr-item-grid" showAddRow={false} showDeleteRow={false} footerSummary={[{ field: 'value', type: 'sum', header: 'Total Return Value' }]} />
        </div>

        <div className="erp-form-card" style={{ padding: 'var(--erp-sp-3)', display: 'flex', justifyContent: 'flex-end' }}>
          <div className="erp-form-row" style={{ margin: 0 }}><label className="erp-form-label" style={{ fontWeight: 700 }}>Total Return Amount</label><div className="erp-form-control"><span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 14, fontWeight: 700, color: 'var(--erp-primary)' }} data-testid="pbr-total-amt">{fmtN(returnAmount)}</span></div></div>
        </div>
      </div>

      {showPBLookup && <ERPLookupModal title="PARTY BILL LOOKUP" columns={PARTY_BILL_COLUMNS} fetchData={fetchPartyBills} onSelect={handlePBSelect} onClose={() => setShowPBLookup(false)} />}
    </div>
    </FormHelp>
  );
};

// ═══════════════════════════════════════════════════════
const PartyBillReturnsScreen = ({ theme = 'light', onBack }) => {
  const [view, setView] = useState('grid');
  const [savedRows, setSavedRows] = useState([]);
  const [editData, setEditData] = useState(null);
  const [formMode, setFormMode] = useState('add');

  if (view === 'grid') return <PBReturnsGrid rows={savedRows} onAddNew={() => { setEditData(null); setFormMode('add'); setView('form'); }} onView={(row) => { setEditData(row); setFormMode('view'); setView('form'); }} />;
  return <PBReturnsForm editData={editData} mode={formMode} onGridView={() => setView('grid')} onSaved={(row) => { setSavedRows(prev => [row, ...prev.filter(r => r.pbrNo !== row.pbrNo)]); setView('grid'); }} />;
};

export default PartyBillReturnsScreen;
